<template>
  <div class="quotes-data-table__container">
    <TableNormal
      class="quotes-data-table__quotes-table"
      :fixed-header="true"
      :headers="headers"
      :items="existingQuotes"
      :items-per-page="itemsPerPage"
      :page.sync="currentPage"
      :single-select="false"
      hide-default-footer
      item-key="id"
      @update:sort-by="setSortingField"
      @update:sort-desc="setSortingOrder"
      :mobile-breakpoint="1280"
      :loading="tableLoading"
      :has-shadow="false"
      :loading-text="$t('opportunity-quotes.loading')"
      :no-data-text="$t('opportunity-quotes.no-quotes')"
      v-model="selectedQuotes"
      :show-select="!isPhone"
    >
      <template v-slot:header.data-table-select="{}">
        <FormCheckbox
          :indeterminate="partiallySelected"
          :afterIndeterminate="false"
          :onChange="onSelectAllClick"
          :value="allAreSelected"
          class="tenders-data-table__check-all"
          v-if="existingQuotes.length > 0"
        />
        <BaseBulkActionMenu v-if="hasSelection" class="base-bulk-action-menu">
          <template slot="content">
            <BaseStatusMenu
              :items="quoteStatuses"
              :current-status="null"
              attach=".tenders-data-table__check-all"
              @change="onBulkStatusChange($event)"
            />
          </template>
        </BaseBulkActionMenu>
      </template>
      <template v-slot:item.data-table-select="{ isSelected, select }">
        <FormCheckbox :value="isSelected" @input="select($event)" @click.native.stop />
      </template>
      <template v-slot:item.potentialBidder="{ item }">
        <!-- Phones: the bidder's name as the row's title; the contact follows, the rest is on desktop. -->
        <span v-if="isPhone">{{ quoteName(item) }}</span>
        <BaseCircularLoadingBar
          v-else-if="opportunity.tenderId && tenderPending"
          :size="14"
          :width="2"
          color="accent"
        />
        <div v-else-if="item.potentialBidder" class="quotes-data-table__potential-bidder">
          <BasePotentialBidder :potentialBidder="item.potentialBidder" :key="item.potentialBidder.id" />
        </div>
        <span v-else>
          {{ $t('basic-words.none') }}
        </span>
      </template>
      <template v-slot:item.createdAt="{ item }">
        <span class="quotes-data-table__item-subtitle">{{ formatStandardDate(item.createdAt) }}</span>
      </template>
      <template v-slot:item.amount="{ item }">
        <span class="quotes-data-table__item-subtitle number-monospace">{{ formatAmount(item.totalAmount) }}</span>
      </template>
      <template v-slot:item.contact="{ item }">
        <div
          v-if="item.contactFirstName && !(item.quoteSync && hasSyncProfiles)"
          class="quotes-data-table__content-vertical"
        >
          <span class="quotes-data-table__item-title">{{ item.contactFirstName }} {{ item.contactLastName }}</span>
          <span v-if="item.contactEmail" class="quotes-data-table__item-subtitle">{{ item.contactEmail }}</span>
        </div>
        <span v-else>-</span>
      </template>
      <template v-slot:item.status="{ item }">
        <div :class="`status-${item.id}`">
          <BaseStatusMenu
            ref="opportunityStatusMenu"
            :items="quoteStatuses"
            :current-status="selectedStatus(item.status)"
            :attach="`.status-${item.id}`"
            @change="onStatusChange(item, $event)"
          />
        </div>
      </template>
      <template v-slot:item.synchronization="{ item }">
        <div v-if="item.quoteSync" class="quotes-data-table__sync-details">
          <BaseRoundButton
            is-white
            :is-neutral="getQuoteSyncNeutralClass(item.quoteSync)"
            :size="44"
            :disabled="!isParentOpportunitySynced(item.quoteSync)"
            :icon="getQuoteSyncIcon(getSyncProfile(item.quoteSync.profileId), item.quoteSync)"
            :tooltip-text="getQuoteSyncButtonTooltip(item.quoteSync)"
            @click.native="onSynchronizeQuoteClick(getSyncProfile(item.quoteSync.profileId), item)"
          />
          <div class="quotes-data-table__sync-info">
            <div class="quotes-data-table__sync-section">
              <span class="quotes-data-table__sync-label">{{ $t('quotes.data-table-titles.client') }}</span>
              <div class="quotes-data-table__sync-box" :class="{ 'is-synced': isQuoteAccountSynced(item) }">
                <span>{{ item.accountName }}</span>
                <BaseIcon v-if="isQuoteAccountSynced(item)" name="check" class="quotes-data-table__sync-check" />
              </div>
            </div>
            <div class="quotes-data-table__sync-section">
              <span class="quotes-data-table__sync-label">{{ $t('quotes.data-table-titles.contact') }}</span>
              <div class="quotes-data-table__sync-box" :class="{ 'is-synced': isQuoteContactSynced(item) }">
                <div>
                  <div>{{ item.contactFirstName }} {{ item.contactLastName }}</div>
                  <div v-if="item.contactEmail" class="quotes-data-table__sync-contact-email">
                    {{ item.contactEmail }}
                  </div>
                </div>
                <BaseIcon v-if="isQuoteContactSynced(item)" name="check" class="quotes-data-table__sync-check" />
              </div>
            </div>
          </div>
        </div>
        <span v-else-if="!isPhone">-</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <BaseMoreActions
          v-if="isPhone"
          :title="quoteName(item)"
          :actions="rowActions(item)"
          @select="onRowAction($event, item)"
        />
        <v-menu v-else>
          <template v-slot:activator="{ on, attrs }">
            <div class="opportunity-header__action" @click.stop v-on="on" v-bind="attrs">
              <BaseIcon class="opportunity-header__action-icon" name="more-horizontal" />
            </div>
          </template>
          <div class="base-actions-menu">
            <ul class="base-actions-menu__ul">
              <li v-if="!item.quoteSync" class="base-actions-menu__li" @click="onEditManualQuote(item)">
                <BaseIcon name="edit" />
                {{ $t('actions.edit') }}
              </li>
              <li class="base-actions-menu__li delete" @click="onDeleteQuote(item)">
                <BaseIcon name="trash-2" />
                {{ $t('actions.delete') }}
              </li>
            </ul>
          </div>
        </v-menu>
      </template>
    </TableNormal>

    <template v-if="this.opportunity.tenderId">
      <div class="quotes-data-table__suggestions-label">{{ $t('quotes.suggestions') }}</div>
      <TableNormal
        class="quotes-data-table__suggestions-table"
        :headers="suggestionHeaders"
        :items="unassignedPotentialBidders"
        :single-select="false"
        hide-default-footer
        item-key="id"
        :mobile-breakpoint="720"
        :loading="quotesPending || tenderPending || unassignedPotentialBiddersPending"
        :loading-text="$t('opportunity-quotes.suggestions-loading')"
        :no-data-text="$t('opportunity-quotes.no-suggestions')"
      >
        <template v-slot:item.potentialBidder="{ item }">
          <div class="quotes-data-table__potential-bidder">
            <BasePotentialBidder v-if="item" :potentialBidder="item" :key="item.id" :source="item.source" />
            <span v-else>
              {{ $t('basic-words.none') }}
            </span>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="quotes-data-table__cell-item">
            <div v-if="hasSyncProfiles" class="quotes-data-table__split-button">
              <BaseButton
                is-small
                class="quotes-data-table__split-button-main"
                @click.native="onCreateQuoteAndSync(item)"
              >
                {{ $t('quotes.create-and-sync-quote') }}
              </BaseButton>
              <div class="quotes-data-table__split-button-divider"></div>
              <v-menu offset-y :disabled="isPhone">
                <template v-slot:activator="{ on, attrs }">
                  <div v-on="on" v-bind="attrs" @click="openQuoteSheet(item)">
                    <BaseButton is-small class="quotes-data-table__split-button-arrow">
                      <BaseIcon name="chevron-down" />
                    </BaseButton>
                  </div>
                </template>
                <div class="base-actions-menu">
                  <ul class="base-actions-menu__ul">
                    <li class="base-actions-menu__li" @click="onCreateManualQuote(item)">
                      {{ $t('quotes.create-quote') }}
                    </li>
                  </ul>
                </div>
              </v-menu>
            </div>
            <BaseButton v-else is-small @click.native="onCreateManualQuote(item)">
              {{ $t('quotes.create-quote') }}
            </BaseButton>
          </div>
        </template>
      </TableNormal>
    </template>

    <!-- Phones: the split button's other way, in a sheet named after the bidder (docs/page-layout.md). -->
    <BaseBottomSheet
      v-if="isPhone"
      :value="!!quoteSheetBidder"
      :title="quoteSheetBidder ? quoteSheetBidder.organization_name : ''"
      @input="closeQuoteSheet"
    >
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="onSheetCreateManualQuote">
            <BaseIcon name="plus" />
            {{ $t('quotes.create-quote') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>

    <CreateAndSyncQuoteDialog ref="createAndSyncQuoteDialog" @quote-created="updateQuotes" />
    <CreateManualQuoteDialog ref="createManualQuoteDialog" @quote-created="updateQuotes" />
    <SyncQuoteDialog ref="syncQuoteDialog" @confirm="onConfirmSyncQuote" />
    <ConfirmationDialog ref="deleteQuoteDialog" :buttonText="$t('actions.delete')" @execute="executeDeleteQuote">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="opportunity-quotes.delete-quote-confirmation">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseBulkActionMenu from '@/components/page-tenders/BaseBulkActionMenu.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import BaseButton from '@/components/base/BaseButton.vue';
import BasePotentialBidder from '@/components/base/BasePotentialBidder.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import CreateManualQuoteDialog from '@/components/page-opportunity-quotes/CreateManualQuoteDialog.vue';
import CreateAndSyncQuoteDialog from '@/components/page-opportunity-quotes/CreateQuoteDialog.vue';
import SyncQuoteDialog from '@/components/page-opportunity-quotes/SyncQuoteDialog.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import TableNormal from '@/components/table/TableNormal.vue';

import { STATUS, STATUS_COLORS, STATUS_VALUES } from '@/models/quotes-status';
import { getEntitySyncIcon, getEntitySyncNeutralClass, getEntitySyncTooltip } from '@/util/entity-sync-util';
import { mapActions, mapGetters } from 'vuex';
import { formatStandardDate } from '@/util/date-util';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';

export default {
  name: 'QuotesDataTable',
  components: {
    BaseBottomSheet,
    BaseMoreActions,
    BaseBulkActionMenu,
    BaseCircularLoadingBar,
    BaseIcon,
    BaseButton,
    BasePotentialBidder,
    BaseRoundButton,
    BaseStatusMenu,
    ConfirmationDialog,
    CreateManualQuoteDialog,
    CreateAndSyncQuoteDialog,
    SyncQuoteDialog,
    FormCheckbox,
    TableNormal,
  },
  data() {
    return {
      itemsPerPage: 25,
      currentPage: null,
      manipulatedQuote: null,
      selectedQuotes: [],
      // The suggestion whose sheet is open, on phones.
      quoteSheetBidder: null,
    };
  },
  computed: {
    // Phones select no quotes: each row changes its own status, edits and deletes itself.
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('biDashboard', { biTender: 'tender' }),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('tender', ['tender']),
    ...mapGetters('tender', { tenderPending: 'pending' }),
    ...mapGetters('quotes', ['quotes']),
    ...mapGetters('quotes', { quotesPending: 'pending' }),
    ...mapGetters('quoteSync', ['quoteSyncs']),
    ...mapGetters('opportunitySyncProfile', { opportunitySyncProfilePending: 'pending' }),
    ...mapGetters('opportunitySyncProfiles', ['opportunitySyncProfiles']),
    ...mapGetters('opportunitySync', ['opportunitySyncs']),
    ...mapGetters('unassignedPotentialBidders', ['unassignedPotentialBidders']),
    ...mapGetters('unassignedPotentialBidders', { unassignedPotentialBiddersPending: 'pending' }),
    tableLoading() {
      return this.opportunitySyncProfilePending;
    },
    headers() {
      const headers = [
        {
          text: this.$t('quotes.data-table-titles.potential-bidder'),
          value: 'potentialBidder',
          sortable: false,
        },
        {
          text: this.$t('quotes.data-table-titles.creation-date'),
          value: 'createdAt',
          sortable: false,
          width: '6%',
        },
        {
          text: this.$t('quotes.data-table-titles.amount'),
          value: 'amount',
          phone: 'subtitle',
          sortable: false,
          width: '8%',
        },
        {
          text: this.$t('quotes.data-table-titles.contact'),
          value: 'contact',
          phone: 'block',
          sortable: false,
          width: this.hasSyncProfiles ? '14%' : '20%',
        },
      ];

      if (this.hasSyncProfiles) {
        headers.push({
          text: this.$t('quotes.data-table-titles.synchronization'),
          value: 'synchronization',
          phone: 'block',
          sortable: false,
          width: '25%',
        });
      }

      headers.push(
        {
          text: this.$t('quotes.data-table-titles.status'),
          value: 'status',
          phone: 'meta',
          sortable: false,
          width: '5%',
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          width: '5%',
        },
      );

      return headers;
    },
    suggestionHeaders() {
      return [
        {
          text: this.$t('quote-sync.potential-bidder'),
          value: 'potentialBidder',
          sortable: false,
        },
        {
          text: this.$t('page.opportunities.data-table-titles.actions'),
          value: 'actions',
          // Phones: the buttons to create a quote, across the row.
          phone: 'block',
          width: '15%',
          sortable: false,
        },
      ];
    },
    existingQuotes() {
      return this.quotes.map((quote) => {
        let builtQuote = { ...quote };

        if (this.biTender && this.biTender.interested_tenderers && quote.sourceEntity === 'potential_bidder') {
          const tenderPotentialBidders = this.biTender ? this.biTender.interested_tenderers : [];
          const potentialBidders = tenderPotentialBidders.concat(this.opportunity.potentialBidders);

          const potBidder = potentialBidders.find((pb) => pb.id === quote.sourceId);

          builtQuote = {
            ...quote,
            potentialBidder: potBidder,
          };
        }
        // Check if quote has a quoteSync object related
        const quoteSync = this.quoteSyncs.find((quoteSync) => quoteSync.quoteId === quote.id);
        if (quoteSync) {
          builtQuote = { ...builtQuote, quoteSync };
        }
        return builtQuote;
      });
    },
    quoteStatuses() {
      return STATUS_VALUES.map((status) => ({
        name: this.$t(`quotes.statuses.${status}`),
        color: STATUS_COLORS[status],
        id: status,
      }));
    },
    activeSyncProfiles() {
      return this.opportunitySyncProfiles.filter((profile) => profile.active);
    },
    hasSyncProfiles() {
      return this.activeSyncProfiles.length > 0;
    },
    syncs() {
      return this.activeSyncProfiles.map((profile) => {
        const syncObj = this.getParentOpportunitySync(profile);
        return { profile, sync: syncObj };
      });
    },
    hasSelection() {
      return this.selectedQuotes.length > 0;
    },
    partiallySelected() {
      return this.hasSelection && this.existingQuotes.length !== this.selectedQuotes.length;
    },
    allAreSelected() {
      return this.existingQuotes.length === this.selectedQuotes.length;
    },
  },
  methods: {
    openQuoteSheet(bidder) {
      if (this.isPhone) this.quoteSheetBidder = bidder;
    },
    closeQuoteSheet() {
      this.quoteSheetBidder = null;
    },
    onSheetCreateManualQuote() {
      const bidder = this.quoteSheetBidder;
      this.closeQuoteSheet();
      this.onCreateManualQuote(bidder);
    },
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        ...(item.quoteSync ? [] : [{ key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' }]),
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, item) {
      if (key === 'edit') this.onEditManualQuote(item);
      else this.onDeleteQuote(item);
    },
    ...mapActions('quoteSync', ['getQuoteSyncs', 'syncQuote']),
    ...mapActions('quotes', ['deleteQuote', 'updateQuote', 'updateQuoteBulk']),
    ...mapActions('tender', ['getTender']),
    ...mapActions('unassignedPotentialBidders', ['getUnassignedPotentialBidders']),
    ...mapActions('biDashboard', { getBiTender: 'getTender' }),
    getEntitySyncTooltip,
    getEntitySyncIcon,
    getEntitySyncNeutralClass,
    formatStandardDate,
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '-';
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 0,
      }).format(amount);
    },
    isQuoteAccountSynced(item) {
      if (!item?.quoteSync) return false;
      return item.quoteSync.status === 'SUCCESS' && Boolean(item.accountName);
    },
    isQuoteContactSynced(item) {
      if (!item?.quoteSync) return false;
      return item.quoteSync.status === 'SUCCESS' && Boolean(item.contactFirstName || item.contactLastName);
    },
    setSortingField() {},
    setSortingOrder() {},
    onSelectAllClick() {
      if (this.partiallySelected || this.allAreSelected) this.unselectAll();
      else this.selectAll();
    },
    selectAll() {
      this.selectedQuotes = [...this.existingQuotes];
    },
    unselectAll() {
      this.selectedQuotes = [];
    },
    selectedStatus(status) {
      return this.quoteStatuses.find((quoteStatus) => quoteStatus.id === STATUS[status]);
    },
    getSyncProfile(profileId) {
      return this.syncs.find((sync) => sync.profile.id === profileId);
    },
    getSuggestedQuotes() {
      const tenderPotentialBidders =
        this.opportunity.tenderId && this.biTender && this.biTender.interested_tenderers
          ? this.biTender.interested_tenderers
          : [];
      const potentialBidders = tenderPotentialBidders.concat(this.opportunity.potentialBidders);

      this.getUnassignedPotentialBidders({
        quotes: this.quotes,
        potentialBidders,
      });
    },
    onCreateQuoteAndSync(potentielBidder) {
      this.$refs.createAndSyncQuoteDialog.openDialog(potentielBidder);
    },
    onCreateManualQuote(potentielBidder) {
      this.$refs.createManualQuoteDialog.openDialog(potentielBidder);
    },
    onEditManualQuote(item) {
      this.$refs.createManualQuoteDialog.editDialog(item);
    },
    onDeleteQuote(quote) {
      this.manipulatedQuote = quote;
      this.$refs.deleteQuoteDialog.openDialog();
    },
    updateQuotes() {
      this.getSuggestedQuotes();
      this.fetchQuoteSyncs();
    },
    fetchQuoteSyncs() {
      this.getQuoteSyncs({
        profileIds: this.activeSyncProfiles.map((profile) => profile.id),
        quoteIds: this.quotes.map((quote) => quote.id),
      });
    },
    getQuoteSyncIcon(syncProfile, quoteSync) {
      return getEntitySyncIcon(syncProfile.profile, quoteSync);
    },
    getQuoteSyncTooltip(syncProfile, quoteSync) {
      return getEntitySyncTooltip(syncProfile.profile, quoteSync);
    },
    getQuoteSyncNeutralClass(quoteSync) {
      return getEntitySyncNeutralClass(quoteSync);
    },
    onBulkStatusChange(status) {
      const updatesQuotes = this.selectedQuotes.map((quote) => ({ ...quote, status: status.id }));
      this.updateQuoteBulk({ opportunityId: this.opportunity.id, quotes: updatesQuotes });
      this.unselectAll();
    },
    // Who the quote is from, as the desktop's bidder card names it.
    quoteName(item) {
      return item.accountName || item.name || (item.potentialBidder && item.potentialBidder.organization_name) || '';
    },
    onStatusChange(item, status) {
      const updatedQuote = { ...item, status: status.id };
      this.updateQuote({ opportunityId: this.opportunity.id, quoteId: updatedQuote.id, quote: updatedQuote });
    },
    isParentOpportunitySynced(quoteSync) {
      // The parent opportunity must exist in the CRM. Use externalOpportunityId, not status: a failed
      // update still leaves the opportunity in the CRM, so its quotes remain syncable.
      const opportunitySync = this.getSyncProfile(quoteSync.profileId);
      return Boolean(opportunitySync && opportunitySync.sync && opportunitySync.sync.externalOpportunityId);
    },
    getQuoteSyncButtonTooltip(quoteSync) {
      if (!this.isParentOpportunitySynced(quoteSync)) {
        return this.$t('quote-sync.unsynchronized-opportunity-warning');
      }
      return this.getQuoteSyncTooltip(this.getSyncProfile(quoteSync.profileId), quoteSync);
    },
    onSynchronizeQuoteClick(opportunitySync, quote) {
      const quoteSync = quote.quoteSync;
      if (opportunitySync.sync && opportunitySync.sync.status && opportunitySync.sync.status === 'IN_PROGRESS') {
        this.$toast.warning(
          this.$t('opportunity-sync.statuses.in-progress', { date: opportunitySync.sync.lastSyncDate }),
        );
        return;
      }
      if (quoteSync && quoteSync.status === 'IN_PROGRESS') {
        this.$toast.warning(this.$t('quote-sync.statuses.in-progress', { date: quoteSync.lastSyncDate }));
        return;
      }
      // New/failed: open the form to set fields. Success: re-sync directly (backend reuses stored values).
      if (!quoteSync || quoteSync.status === 'FAILED') {
        this.$refs.syncQuoteDialog.openDialog(quote, quoteSync, opportunitySync.profile);
      } else {
        this.executeSynchronizeQuote(quote, opportunitySync.profile, {});
      }
    },
    onConfirmSyncQuote(quote, profile, fieldValues) {
      this.executeSynchronizeQuote(quote, profile, fieldValues);
    },
    executeSynchronizeQuote(quote, profile, fieldValues = {}) {
      this.syncQuote({
        opportunitySyncProfileId: profile.id,
        quoteId: quote.id,
        fieldValues,
      }).then(() => {
        this.$toast.success(this.$t('quote-sync.success'));
        this.fetchQuoteSyncs();
      });
    },
    executeDeleteQuote() {
      this.deleteQuote({
        opportunityId: this.opportunity.id,
        quoteId: this.manipulatedQuote.id,
      })
        .then(() => {
          this.$toast.success(this.$t('opportunity-quotes.quote-manipulation.delete-success'));
          this.getSuggestedQuotes();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    getParentOpportunitySync(profile) {
      return this.opportunitySyncs.find(
        (oppSync) => oppSync.profileId === profile.id && oppSync.opportunityId === this.opportunity.id,
      );
    },
  },
  watch: {
    // When chrome extension updates the potential bidders, the tender changes.
    tender() {
      this.fetchQuoteSyncs();
      this.getSuggestedQuotes();
    },
    biTender() {
      this.getSuggestedQuotes();
    },
  },
  async created() {
    // Get suggested quotes depending on potential bidders if the opportunity has a tender
    if (this.opportunity.tenderId) {
      await this.getTender({ id: this.opportunity.tenderId });
      await this.getBiTender({ id: this.opportunity.tenderId });
    }
    this.getSuggestedQuotes();
  },
};
</script>

<style lang="scss">
.quotes-data-table {
  &__container {
    display: flex;
    flex-direction: column;
    margin-bottom: 64px;
  }

  &__actions-button {
    max-width: min-content;
  }

  &__sync-details {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
  }

  &__sync-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__sync-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__sync-label {
    font-size: $font-size-small;
    font-weight: 700;
    color: $color-normal;
  }

  &__sync-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border-radius: $border-radius-medium;
    background: $color-neutral-pale;
    font-size: $font-size-small;
    font-weight: 700;
    color: $color-normal;

    &.is-synced {
      background: rgba(132, 210, 165, 0.3);
    }
  }

  &__sync-check {
    height: 14px;
    width: 14px;
    color: $color-green;
    flex-shrink: 0;
  }

  &__sync-contact-email {
    color: $color-dark-blue;
    font-weight: 400;
  }

  &__item {
    &-title {
      color: $color-normal;
      font-size: 0.875rem;
      font-weight: 600;
    }

    &-subtitle {
      color: $color-dark-blue;
      font-size: 0.875rem;
      font-weight: 600;
    }

    &-url {
      color: $color-accent-500;
    }
  }

  &__content-vertical {
    display: flex;
    flex-direction: column;

    &.synchronized {
      border-radius: 5px;
      min-height: 42px;
      padding: 8px;
      background: $color-accent-100;
    }
  }

  &__synchronized-quote-item {
    height: 14px;
    width: 14px;
    color: $color-accent;
    justify-self: flex-end;
    margin-left: auto;
    display: flex;
  }

  &__suggestions-label {
    padding: 8px;
    padding-top: 12px;
    font-size: 14px;
    color: $color-normal-pale;
    width: 100%;
  }

  &__quotes-table {
    border-radius: 5px !important;
    z-index: 1;
  }

  &__quotes-table {
    &.v-data-table {
      tbody {
        cursor: default !important;
      }

      .v-data-table__mobile-row {
        width: 100%;
      }
    }
  }

  &__suggestions-table {
    padding: 0px !important;
    border: 1px solid $color-border;
    z-index: 0;

    .v-data-table__wrapper {
      border-radius: 8px;
    }

    &.v-data-table {
      background: $color-hover;

      tbody {
        cursor: default !important;
      }

      thead {
        background: $color-white;
      }
    }
  }

  &__potential-bidder {
    max-width: 450px;
  }

  &__split-button {
    display: inline-flex;
    align-items: stretch;
    border-radius: $border-radius-large;
    overflow: hidden;

    &-main {
      white-space: nowrap;
    }

    &-main,
    &-arrow {
      .base-button__element {
        border-radius: 0;
      }
    }

    &-arrow {
      .base-button__element {
        padding: 4px 6px;
      }

      .feather {
        height: 17px;
        width: 14px;
      }
    }

    &-divider {
      width: 1px;
      background-color: $color-neutral-pale;
    }
  }
}
</style>
